/**
 * sourceToViewport - the coordinate model's core (D-05, RS-10).
 *
 * Every shape (pointer, annotation, frozen-frame overlay) is stored as a NormPoint: x/y in
 * 0..1 against the UNMIRRORED, UNROTATED canonical source image. This function builds a
 * ViewportTransform that maps a NormPoint to a pixel position inside a given ViewportRect,
 * and back, so every screen that displays the same source frame agrees on where a shape is -
 * regardless of the viewport's own aspect ratio, orientation, rotation or mirroring.
 *
 * Rotation is applied before mirroring. Mirroring is a per-view DISPLAY property (only the
 * owner of a front camera sees it mirrored, see 05-13 displayGeometry) and is never stored;
 * stored points stay canonical, so mirrored and unmirrored viewers round-trip the same point.
 *
 * Contain-fit (the letterboxing math below) matches CSS `object-fit: contain`, which the
 * stage already uses (Phase 4 D-03).
 */

export type Rotation = 0 | 90 | 180 | 270;

export type FrameGeometry = {
  sourceW: number;
  sourceH: number;
  mirrored: boolean;
  rotation: Rotation;
};

export type ViewportRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type NormPoint = {
  x: number;
  y: number;
};

export type ViewportPoint = {
  x: number;
  y: number;
};

export type ViewportTransform = {
  valid: boolean;
  contentRect: ViewportRect;
  scale: number;
  toViewport(p: NormPoint): ViewportPoint;
  toSource(v: ViewportPoint): NormPoint;
  isInside(p: NormPoint): boolean;
};

function displayedSize(frame: FrameGeometry): { dw: number; dh: number } {
  if (frame.rotation === 90 || frame.rotation === 270) {
    return { dw: frame.sourceH, dh: frame.sourceW };
  }
  return { dw: frame.sourceW, dh: frame.sourceH };
}

// Forward: normalized source (u, v) -> normalized display (a, b), before mirroring.
function forwardRotate(rotation: Rotation, u: number, v: number): { a: number; b: number } {
  switch (rotation) {
    case 0:
      return { a: u, b: v };
    case 90:
      return { a: 1 - v, b: u };
    case 180:
      return { a: 1 - u, b: 1 - v };
    case 270:
      return { a: v, b: 1 - u };
  }
}

// Inverse: normalized display (a, b), after un-mirroring -> normalized source (u, v).
function inverseRotate(rotation: Rotation, a: number, b: number): { u: number; v: number } {
  switch (rotation) {
    case 0:
      return { u: a, v: b };
    case 90:
      return { u: b, v: 1 - a };
    case 180:
      return { u: 1 - a, v: 1 - b };
    case 270:
      return { u: 1 - b, v: a };
  }
}

function isFinitePositive(n: number): boolean {
  return Number.isFinite(n) && n > 0;
}

export function sourceToViewport(
  frame: FrameGeometry,
  viewportRect: ViewportRect,
): ViewportTransform {
  const allFinite =
    Number.isFinite(frame.sourceW) &&
    Number.isFinite(frame.sourceH) &&
    Number.isFinite(viewportRect.x) &&
    Number.isFinite(viewportRect.y) &&
    Number.isFinite(viewportRect.width) &&
    Number.isFinite(viewportRect.height);

  const valid =
    allFinite &&
    isFinitePositive(frame.sourceW) &&
    isFinitePositive(frame.sourceH) &&
    isFinitePositive(viewportRect.width) &&
    isFinitePositive(viewportRect.height);

  if (!valid) {
    const contentRect: ViewportRect = {
      x: Number.isFinite(viewportRect.x) ? viewportRect.x : 0,
      y: Number.isFinite(viewportRect.y) ? viewportRect.y : 0,
      width: 0,
      height: 0,
    };
    return {
      valid: false,
      contentRect,
      scale: 0,
      toViewport: () => ({ x: contentRect.x, y: contentRect.y }),
      toSource: () => ({ x: 0, y: 0 }),
      isInside: (p: NormPoint) => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1,
    };
  }

  const { dw, dh } = displayedSize(frame);
  const scale = Math.min(viewportRect.width / dw, viewportRect.height / dh);
  const contentW = dw * scale;
  const contentH = dh * scale;
  const contentRect: ViewportRect = {
    x: viewportRect.x + (viewportRect.width - contentW) / 2,
    y: viewportRect.y + (viewportRect.height - contentH) / 2,
    width: contentW,
    height: contentH,
  };

  const toViewport = (p: NormPoint): ViewportPoint => {
    const { a: rawA, b } = forwardRotate(frame.rotation, p.x, p.y);
    const a = frame.mirrored ? 1 - rawA : rawA;
    return {
      x: contentRect.x + a * contentRect.width,
      y: contentRect.y + b * contentRect.height,
    };
  };

  const toSource = (v: ViewportPoint): NormPoint => {
    let a = (v.x - contentRect.x) / contentRect.width;
    const b = (v.y - contentRect.y) / contentRect.height;
    if (frame.mirrored) {
      a = 1 - a;
    }
    const { u, v: sourceV } = inverseRotate(frame.rotation, a, b);
    return { x: u, y: sourceV };
  };

  const isInside = (p: NormPoint): boolean => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1;

  return { valid: true, contentRect, scale, toViewport, toSource, isInside };
}
